import { useEffect, useState } from "react";
import { Check, Loader2, Sparkles, Trash2 } from "lucide-react";
import { http, errorText } from "@/lib/adminApi";

const field = "w-full rounded-xl border border-white/15 bg-[var(--ink)] px-4 py-3 text-white placeholder:text-slate-500 focus:border-[var(--teal)] focus:outline-none";

const Draft = ({ d, onPublished, onDiscard }) => {
  const [f, setF] = useState({ title: d.title, description: d.description, before: d.before, after: d.after, body: d.body });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const publish = async () => {
    setBusy(true);
    setMsg("");
    try { const { data } = await http.post(`/admin/tip-drafts/${d.id}/publish`, f); onPublished(d.id, data); }
    catch (err) { setMsg(errorText(err)); setBusy(false); }
  };
  return (
    <div className="space-y-3 rounded-2xl border border-[var(--teal)]/40 bg-[var(--navy)] p-5 sm:p-6" data-testid={`tip-draft-${d.id}`}>
      <p className="text-xs uppercase tracking-widest text-[var(--teal)]">AI draft · {d.source === "weekly" ? "weekly" : "on demand"} · {d.created_at?.slice(0, 10)}</p>
      <input className={field} value={f.title} onChange={set("title")} data-testid="draft-title-input" />
      <input className={field} value={f.description} onChange={set("description")} data-testid="draft-description-input" />
      <div className="grid gap-3 sm:grid-cols-2">
        <textarea rows={3} className={field} value={f.before} onChange={set("before")} data-testid="draft-before-input" />
        <textarea rows={3} className={field} value={f.after} onChange={set("after")} data-testid="draft-after-input" />
      </div>
      <textarea rows={9} className={field} value={f.body} onChange={set("body")} data-testid="draft-body-input" />
      {msg && <p className="text-sm text-[var(--amber)]" role="alert">{msg}</p>}
      <div className="flex flex-wrap gap-2">
        <button onClick={publish} disabled={busy} className="inline-flex items-center gap-2 rounded-full bg-[var(--teal)] px-5 py-2.5 text-sm font-semibold text-[var(--ink)] disabled:opacity-60" data-testid="draft-publish-button">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Publish
        </button>
        <button onClick={() => onDiscard(d.id)} className="inline-flex items-center gap-2 rounded-full border border-white/20 px-4 py-2 text-sm text-slate-300" data-testid="draft-discard-button">
          <Trash2 className="h-4 w-4" /> Discard
        </button>
      </div>
    </div>
  );
};

export const TipDrafts = ({ onPublished }) => {
  const [drafts, setDrafts] = useState([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  useEffect(() => {
    http.get("/admin/tip-drafts").then((r) => setDrafts(Array.isArray(r.data?.drafts) ? r.data.drafts : [])).catch(() => setDrafts([]));
  }, []);
  const generate = async () => {
    setBusy(true);
    setMsg("");
    try { const { data } = await http.post("/admin/tip-drafts"); setDrafts((l) => [data, ...l]); }
    catch (err) { setMsg(errorText(err)); }
    setBusy(false);
  };
  const discard = async (id) => {
    setDrafts((l) => l.filter((d) => d.id !== id));
    try { await http.delete(`/admin/tip-drafts/${id}`); } catch { /* already hidden */ }
  };
  const published = (id, post) => { setDrafts((l) => l.filter((d) => d.id !== id)); onPublished(post); };
  return (
    <div className="space-y-4" data-testid="tip-drafts">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-slate-300">A fresh AI draft appears here every Monday morning. Nothing goes live until you publish it.</p>
        <button onClick={generate} disabled={busy} className="inline-flex items-center gap-2 rounded-full border border-[var(--teal)] px-5 py-2.5 text-sm font-semibold text-[var(--teal)] disabled:opacity-60" data-testid="draft-generate-button">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} {busy ? "Writing draft..." : "Draft a tip with AI"}
        </button>
      </div>
      {msg && <p className="text-sm text-[var(--amber)]" role="alert" data-testid="draft-error">{msg}</p>}
      {drafts.map((d) => <Draft key={d.id} d={d} onPublished={published} onDiscard={discard} />)}
    </div>
  );
};
